import type { EnumContainer, IEnumRepository } from "@edaz/services/baseEnumService";
import type { ClassType } from "class-transformer/ClassTransformer";
import type AuthConnector from "data/authConnector";
import type BackOfficeConnector from "data/backOfficeConnector";
import ChargeType from "entities/chargeType";
import Enum from "entities/enum";
import FuelTypeResponse from "entities/fuelTypeResponse";
import PayGateRefundPaymentTemplate from "entities/payGateRefundPaymentTemplate";
import PayGateToTransferRefundPaymentTemplate from "entities/payGateToTransferRefundPaymentTemplate";
import Region from "entities/region";
import type SystemComponent from "entities/systemComponent";
import TransferRefundForRefundOfRefundPaymentTemplate from "entities/transferRefundForRefundOfRefundPaymentTemplate";
import TransferRefundPaymentTemplate from "entities/transferRefundPaymentTemplate";
import pluralize from "pluralize";
import type { AllEnums, SimpleListEnumTypes, SystemEnumTypes } from "services/enumService";
import type EventBus from "services/eventBus";
import AccountingTemplateResponse from "../entities/accountingTemplateResponse";
import CountryResponse from "../entities/countryResponse";
import type IncludeDeletedPaginatedRequest from "../entities/includeDeletedPaginatedRequest";
import RepositoryBase from "./repositoryBase";
import Municipality from "entities/municipality";
import Road from "entities/road";
import Kiosk from "entities/kiosk";
import { SimpleListEnums } from "config";
import BankAccountResponse from "entities/bankAccountResponse";
import PaymentTemplateSimpleListResponse from "entities/paymentTemplateSimpleListResponse";
import type PaymentTemplatesSimpleListResponse from "entities/paymentTemplatesSimpleListResponse";
import DistributorSimpleListResponse from "entities/distributorSimpleListResponse";
import RegionSimpleListResponse from "entities/regionSimpleListResponse";
import RoadsSimpleListResponse from "entities/roadsSimpleListResponse";
import NotifiableExemptionReasonSimpleList from "entities/notifiableExemptionReasonSimpleList";
import type NotifiableExemptionReasonsSimpleList from "entities/notifiableExemptionReasonsSimpleList";
import InventoryAccountsResponse from "entities/InventoryAccountsResponse";

export default class EnumRepository extends RepositoryBase<BackOfficeConnector> implements IEnumRepository<Enum> {
  constructor(
    protected apiFactory: ReturnType<typeof BackOfficeConnector.Factory>,
    protected eventBus: EventBus,
    protected apiFactoryAuth: ReturnType<typeof AuthConnector.Factory>
  ) {
    super(apiFactory, eventBus);
  }

  protected callAuthApi<T>(action: (api: AuthConnector) => Promise<T>) {
    return action(this.apiFactoryAuth());
  }

  create<TEnum extends Enum = Enum>(
    entity: Enum,
    enumName: AllEnums,
    type: ClassType<TEnum> = Enum as ClassType<TEnum>,
    version = "1"
  ) {
    return this.callApi(api => api.all("enums").all(enumName).postEntity<TEnum>(entity, type), {
      useGenericErrorMessageHandler: enumName !== "system_components",
      ownApiFactory: () => this.apiFactory({ customVersion: version }),
    });
  }

  getAll<TEnum extends Enum = Enum>(
    enumName: AllEnums,
    type: ClassType<TEnum> = Enum as ClassType<TEnum>,
    queryParams?: any,
    version = "1",
    skipSimpleList = false
  ) {
    const isSimpleListEnum = (enumName: AllEnums): enumName is SimpleListEnumTypes => {
      return SimpleListEnums.includes(enumName as SimpleListEnumTypes);
    };

    if (!skipSimpleList && isSimpleListEnum(enumName)) {
      return this.callApi(
        api => api.all(isSimpleListEnum(enumName) ? `simple_lists/${enumName}` : enumName).getEntities<TEnum>(type, queryParams),
        {
          ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
        }
      );
    }
    if (queryParams?.hasOwnProperty("limit") && queryParams.hasOwnProperty("offset")) {
      return this.callApi(api => api.all("enums").all(enumName).getPagedEntities<TEnum>(type, queryParams), {
        ownApiFactory: () => this.apiFactory({ customVersion: version }),
      });
    } else {
      return this.callApi(
        api =>
          api
            .all("enums")
            .all(enumName === "countries" ? `simple_lists/${enumName}` : enumName)
            .getEntities<TEnum>(type, queryParams),
        {
          ownApiFactory: () => this.apiFactory({ customVersion: version }),
        }
      );
    }
  }

  postAll<TEnum extends Enum = Enum>(
    enumName: AllEnums,
    type: ClassType<TEnum> = Enum as ClassType<TEnum>,
    payload?: any,
    version = "1"
  ) {
    if (payload?.hasOwnProperty("limit") && payload.hasOwnProperty("offset")) {
      return this.callApi(
        api =>
          api
            .all("enums")
            .all(`${pluralize.singular(enumName)}_search`)
            .postPagedEntities<TEnum>(payload, type),
        {
          ownApiFactory: () => this.apiFactory({ customVersion: version }),
        }
      );
    } else {
      return this.callApi(
        api =>
          api
            .all("enums")
            .all(`${pluralize.singular(enumName)}_search`)
            .postEntities<TEnum>(payload, type),
        {
          ownApiFactory: () => this.apiFactory({ customVersion: version }),
        }
      );
    }
  }

  getAllRegions(queryParams?: any) {
    return this.getAll<Region>("regions", Region, queryParams, undefined, false);
  }

  getAllRoads(queryParams?: any) {
    return this.postAll<Road>("roads", Road, queryParams ?? {});
  }

  getAllMunicipality(queryParams?: any) {
    return this.postAll<Municipality>("municipalities", Municipality, queryParams ?? {});
  }
  getAllGateways() {
    return this.callApi(api =>
      api
        .all("simple_lists")
        .all("gateways")
        .get()
        .then((response: any) => {
          return {
            items: response.items.map((item: any) => ({
              id: item.externalGatewayIdent,
              name: item.externalGatewayIdent,
            })),
          };
        })
    );
  }

  getAllKiosks<TEnum extends Enum = Enum>(queryParams?: any) {
    return this.callApi(
      api =>
        api
          .all("simple_lists")
          .all("kiosks")
          .getPagedEntities<TEnum>(Kiosk as unknown as ClassType<TEnum>, queryParams),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "1.1" }),
      }
    );
  }

  getAllNotifiableExemptionReasons(): Promise<NotifiableExemptionReasonSimpleList[]> {
    return this.callApi(
      api =>
        api
          .all("simple_lists")
          .all("notifiable_exemption_reasons")
          .get()
          .then(response => {
            const typedResponse = response as NotifiableExemptionReasonsSimpleList;
            return typedResponse.items;
          }),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  getAllPaymentTemplates(): Promise<PaymentTemplateSimpleListResponse[]> {
    return this.callApi(
      api =>
        api
          .all("simple_lists")
          .all("payment_templates")
          .get()
          .then(response => {
            const typedResponse = response as PaymentTemplatesSimpleListResponse;
            return typedResponse.items;
          }),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  getAllAccountingTemplates(queryParams?: any) {
    return this.getAll<AccountingTemplateResponse>("accounting_templates", AccountingTemplateResponse, queryParams);
  }

  getAllBankAccounts(queryParams?: any) {
    return this.getAll<BankAccountResponse>("bank_accounts", BankAccountResponse, queryParams, "2");
  }

  getAllSystemComponents(queryParams?: any) {
    return this.callApi(
      api =>
        api
          .all("simple_lists")
          .all("system_components")
          .get()
          .then(response => {
            return response as SystemComponent;
          }),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  getAllCountries(queryParams: IncludeDeletedPaginatedRequest) {
    return this.postAll<CountryResponse>("countries", CountryResponse, queryParams, "1.2");
  }

  getTransferRefundForRefundOfRefundPaymentTemplates(queryParams?: any) {
    return this.getAll<TransferRefundForRefundOfRefundPaymentTemplate>(
      "refund_for_refund_of_refund_payment_templates",
      TransferRefundForRefundOfRefundPaymentTemplate,
      queryParams
    );
  }
  getInventoryAccounts(queryParams?: any) {
    return this.getAll<InventoryAccountsResponse>("inventory_accounts", InventoryAccountsResponse, queryParams);
  }

  getTransferRefundPaymentTemplates(queryParams?: any) {
    return this.getAll<TransferRefundPaymentTemplate>(
      "transfer_refund_payment_templates",
      TransferRefundPaymentTemplate,
      queryParams
    );
  }

  getPayGateRefundPaymentTemplates(queryParams?: any) {
    return this.getAll<PayGateRefundPaymentTemplate>(
      "paygate_refund_payment_templates",
      PayGateRefundPaymentTemplate,
      queryParams
    );
  }

  getPayGateToTransferRefundPaymentTemplates(queryParams?: any) {
    return this.getAll<PayGateToTransferRefundPaymentTemplate>(
      "paygate_to_transfer_refund_payment_templates",
      PayGateToTransferRefundPaymentTemplate,
      queryParams
    );
  }

  getAllExternalIdentityProviders(queryParams?: any) {
    return this.callAuthApi(api =>
      api
        .all("enums")
        .all("external_identity_providers")
        .get()
        .then((response: any) => {
          return { items: response.providers.map((provider: string) => ({ id: provider, name: provider })) };
        })
    );
  }

  get<TEnum extends Enum = Enum>(
    id: string,
    enumName: AllEnums,
    type: ClassType<TEnum> = Enum as ClassType<TEnum>,
    version = "1"
  ) {
    return this.callApi(api => api.all("enums").one(enumName, id).getEntity<TEnum>(type), {
      ownApiFactory: () => this.apiFactory({ customVersion: version }),
    });
  }

  update<TEnum extends Enum = Enum>(
    entity: TEnum,
    enumName: AllEnums,
    type: ClassType<TEnum> = Enum as ClassType<TEnum>,
    version = "1"
  ) {
    return this.callApi(api => api.all("enums").one(enumName, entity.id).putEntity<TEnum>(entity, type), {
      useGenericErrorMessageHandler: enumName !== "system_components",
      ownApiFactory: () => this.apiFactory({ customVersion: version }),
    });
  }

  delete(entity: Enum, enumName: AllEnums, version = "1") {
    return this.callApi(api => api.all("enums").one(enumName, entity.id).delete({}), {
      ownApiFactory: () => this.apiFactory({ customVersion: version }),
    });
  }

  getSystemEnum<TEnum extends Enum = Enum>(enumName: SystemEnumTypes | SimpleListEnumTypes, entity?: ClassType<TEnum>) {
    const queryParams: any = {
      limit: 1000,
    };
    let version = "1";
    if (enumName === "countries") {
      version = "1.1";
    }
    if (enumName === "fuel_types") {
      version = "2";
    }

    if (enumName === "charge_types") {
      queryParams.include_deleted = true;
      version = "2";
    } else {
      queryParams.include_deleted = true;
    }
    if (SimpleListEnums.find(simpleList => simpleList === enumName)) {
      queryParams.include_deleted = true;
      version = "2";
    }

    return this.getAll(enumName, entity, queryParams, version).then(result => result.items);
  }

  async fetchEnums(): Promise<EnumContainer<Enum>> {
    const [
      countries,
      charge_types,
      fuel_types,
      payment_templates,
      paygate_refund_payment_templates,
      transfer_refund_payment_templates,
      municipalities,
      distributors,
      regions,
      roads,
      refund_for_refund_of_refund_payment_templates,
      inventory_accounts,
      notifiable_exemption_reasons,
      paygate_to_transfer_refund_payment_templates,
    ] = await Promise.all([
      this.getSystemEnum("countries", CountryResponse),
      this.getSystemEnum("charge_types", ChargeType),
      this.getSystemEnum("fuel_types", FuelTypeResponse),
      this.getSystemEnum("payment_templates", PaymentTemplateSimpleListResponse as unknown as ClassType<Enum>),
      this.getSystemEnum("paygate_refund_payment_templates", PaymentTemplateSimpleListResponse as unknown as ClassType<Enum>),
      this.getSystemEnum("transfer_refund_payment_templates", PaymentTemplateSimpleListResponse as unknown as ClassType<Enum>),
      this.getSystemEnum("municipalities", PaymentTemplateSimpleListResponse as unknown as ClassType<Enum>),
      this.getSystemEnum("distributors", DistributorSimpleListResponse as unknown as ClassType<Enum>),
      this.getSystemEnum("regions", RegionSimpleListResponse as unknown as ClassType<Enum>),
      this.getSystemEnum("roads", RoadsSimpleListResponse as unknown as ClassType<Enum>),
      this.getSystemEnum(
        "refund_for_refund_of_refund_payment_templates",
        PaymentTemplateSimpleListResponse as unknown as ClassType<Enum>
      ),
      this.getSystemEnum("inventory_accounts", InventoryAccountsResponse as unknown as ClassType<Enum>),
      this.getSystemEnum("notifiable_exemption_reasons", NotifiableExemptionReasonSimpleList as unknown as ClassType<Enum>),
      this.getSystemEnum(
        "paygate_to_transfer_refund_payment_templates",
        PaymentTemplateSimpleListResponse as unknown as ClassType<Enum>
      ),
    ]);

    return {
      countries,
      charge_types,
      fuel_types,
      payment_templates,
      paygate_refund_payment_templates,
      transfer_refund_payment_templates,
      municipalities,
      distributors,
      regions,
      roads,
      refund_for_refund_of_refund_payment_templates,
      inventory_accounts,
      notifiable_exemption_reasons,
      paygate_to_transfer_refund_payment_templates,
    };
  }
}
